import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';

/**
 * useCardPositions — computes the Y offset (px) for each comment/suggestion card.
 *
 * Uses ProseMirror doc.descendants() + view.coordsAtPos() to read positions directly
 * from the document model — no DOM queries, immune to mark-rename timing issues.
 *
 * Position is computed as the VERTICAL MIDPOINT of the marked range:
 *   (coordsAtPos(markStart).top + coordsAtPos(markEnd).bottom) / 2
 * This mirrors exactly how TextSelectionBubble positions itself, so confirmed cards
 * land on the same Y as the pending card the user saw before confirming.
 *
 * Returns a map: { [id]: topPx } where topPx is relative to .editor-body's content top.
 * Cards not yet found in the document are omitted from the map (never 0 fallback).
 *
 * @param {object} editor   — TipTap editor instance
 * @param {Array}  items    — array of { id } objects (comments or suggestions)
 * @param {string} attrKey  — the mark attribute key that holds the id
 *                            e.g. 'commentId' for CommentMark, 'suggestionId' for SuggestionExtension
 */
export function useCardPositions({ editor, items, attrKey }) {
  const [positions, setPositions] = useState({});
  const debounceRef = useRef(null);
  const retryRef = useRef(null);
  // Holds last known valid position per id — used as fallback during mark renames
  const prevPositionsRef = useRef({});

  const computePositions = useCallback(() => {
    if (!editor || !editor.view || !editor.state) return;

    const editorDom = editor.view.dom;
    const bodyEl = editorDom.closest('.editor-body');
    if (!bodyEl) return;

    const bodyRect = bodyEl.getBoundingClientRect();
    const scrollTop = bodyEl.scrollTop;

    // Walk the ProseMirror document to collect the start and end positions of each mark.
    // We track the range [start, end) to compute the midpoint, matching the bubble's
    // positioning formula: (coordsAtPos(from).top + coordsAtPos(to).bottom) / 2
    const markRanges = {}; // { [id]: { start, end } }
    editor.state.doc.descendants((node, pos) => {
      if (!node.isText) return;
      node.marks.forEach(mark => {
        const id = mark.attrs?.[attrKey];
        if (!id) return;
        if (!markRanges[id]) {
          markRanges[id] = { start: pos, end: pos + node.nodeSize };
        } else {
          // Extend to the last marked node
          markRanges[id].end = pos + node.nodeSize;
        }
      });
    });

    const newPositions = {};
    let missingCount = 0;

    items.forEach(({ id }) => {
      if (!id) return;

      const range = markRanges[id];
      if (!range) {
        // Mark not in document yet (Yjs mid-sync or mark rename in flight)
        missingCount++;
        if (prevPositionsRef.current[id] !== undefined) {
          newPositions[id] = prevPositionsRef.current[id];
        }
        // No fallback to 0 — card is simply omitted until mark appears
        return;
      }

      try {
        // Midpoint of the marked range — mirrors TextSelectionBubble's formula:
        //   top = (coordsAtPos(from).top + coordsAtPos(to).bottom) / 2
        const startCoords = editor.view.coordsAtPos(range.start);
        const endCoords = editor.view.coordsAtPos(range.end);
        const midpoint = (startCoords.top + endCoords.bottom) / 2;
        const top = midpoint - bodyRect.top + scrollTop;
        newPositions[id] = Math.max(0, top);
      } catch {
        // coordsAtPos throws for out-of-range positions — keep last known
        if (prevPositionsRef.current[id] !== undefined) {
          newPositions[id] = prevPositionsRef.current[id];
        }
      }
    });

    // Update stable cache BEFORE calling setPositions.
    // IMPORTANT: never mutate prevPositionsRef inside the setState updater —
    // React Concurrent Mode may invoke the updater multiple times, corrupting the ref.
    prevPositionsRef.current = { ...newPositions };

    setPositions(prev => {
      const same =
        Object.keys(newPositions).length === Object.keys(prev).length &&
        Object.entries(newPositions).every(([k, v]) => prev[k] === v);
      return same ? prev : newPositions;
    });

    // Retry if marks are missing (Yjs hasn't synced yet)
    if (missingCount > 0 && items.length > 0) {
      clearTimeout(retryRef.current);
      retryRef.current = setTimeout(computePositions, 300);
    }
  }, [editor, items, attrKey]);

  // Recompute on editor transactions (debounced)
  useEffect(() => {
    if (!editor) return;

    const onUpdate = () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(computePositions, 80);
    };

    editor.on('update', onUpdate);
    editor.on('selectionUpdate', onUpdate);

    computePositions();

    return () => {
      editor.off('update', onUpdate);
      editor.off('selectionUpdate', onUpdate);
      clearTimeout(debounceRef.current);
    };
  }, [editor, computePositions]);

  // Recompute on window resize — layout changes affect mark coordinates.
  // NOTE: scroll is intentionally NOT listened to here. Cards are positioned in
  // document-coordinate space (invariant under scroll) and live inside .editor-body
  // which is the single scroll container, so they scroll naturally with the content.
  // Listening to scroll would re-invoke coordsAtPos for off-screen marks whose
  // viewport coordinates are clamped by ProseMirror, producing wrong (drifting) values.
  useEffect(() => {
    window.addEventListener('resize', computePositions);
    return () => {
      window.removeEventListener('resize', computePositions);
      clearTimeout(retryRef.current);
    };
  }, [computePositions]);

  // Recompute synchronously when items change — fires before paint so cards appear
  // at the correct position on first render (no flash at top: 0)
  useLayoutEffect(() => {
    computePositions();
  }, [items, computePositions]);

  return positions;
}
